import React, { useContext, useMemo } from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import Slider from '@wix/da-shared-react/pkg/Slider';
import FolderCard from '../FolderCard';
import AddNewFolderCard from '../AddNewFolderCard';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiDataHook } from '@wix/da-bi/pkg/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { filterOutEmpty } from '../../../../utils/gallection';
import { useGallectionFolders } from '../hooks/useGallectionFolders';

import { isCurrentUserProfileOwner } from '../../../../selectors/users';

import s from './FolderSlider.scss';

export interface Props {
  className?: string;
  cardWidth?: number;
}

const FolderSlider: React.FC<Props> = ({
  className,
  cardWidth: cardWidthFromProps,
}) => {
  const disableLoaders = useFlag('yumeruby'); // for debugging...
  const isOwner = useSelector(isCurrentUserProfileOwner);

  const {
    hasMore,
    hasLess,
    fetchNext,
    fetchPrevious,
    isFetching,
    folders,
    selectedFolderId,
  } = useGallectionFolders();
  const foldersToRender = useMemo(() => filterOutEmpty(folders), [folders]);

  const isMobile = useContext(MobileContext);
  const cardWidth = useMemo(
    () => cardWidthFromProps || parseInt(s['folder-card-width']),
    [isMobile, cardWidthFromProps]
  );
  const scrollToIndex = foldersToRender.findIndex(
    ({ folderId }) => folderId === selectedFolderId
  );

  return (
    <Slider
      className={c(s['folders-slider'], className)}
      scrollToIndex={scrollToIndex}
      smoothInitialScroll={false}
      dataHook={BiDataHook.PROFILE_GALLECTION_FOLDER}
    >
      {hasLess && !disableLoaders && (
        <Waypoint onEnter={fetchPrevious} horizontal topOffset="-20%" />
      )}
      {foldersToRender.map((folder, index) => (
        <FolderCard
          withPrice
          width={cardWidth}
          className={s['folder-card']}
          key={folder.folderId}
          folder={folder}
          indexInsideParent={index}
          dataHook={BiDataHook.PROFILE_GALLECTION_FOLDER_ITEM + index}
          withCommentsCount={isMobile}
          withLink
        />
      ))}
      {isFetching && <LoadingIndicator className={s['folder-card']} />}
      {hasMore && !disableLoaders && (
        <Waypoint onEnter={fetchNext} horizontal bottomOffset="-20%" />
      )}
      {isOwner && (
        <AddNewFolderCard className={s['folder-card']} width={cardWidth} />
      )}
    </Slider>
  );
};
FolderSlider.displayName = 'FolderSlider';

export default FolderSlider;
